import React from 'react';
import { Select, Button, Input, DatePicker, Link, ButtonDropdown } from '@amzn/awsui-components-react/polaris';
import { SelectProps } from '@amzn/awsui-components-react/polaris/select';
import CONSTANTS from 'src/utils/constants';
import {
    getBalancePullingStatus, getValueDisplay, performRowAction, RowActions,
} from 'src/components/tp-allocation/shared/DatasetGridCommon';
import { DatasetRecord } from 'src/models/tp-allocation/DatasetRecord';
import { TPLOV } from 'src/models/common/TPLOV';
import TPEAction from 'src/models/common/TPEAction';

/** A Step 1 grid row: a dataset record plus the display values the grid adds. */
export type DasDataSetRow = DatasetRecord & {
    sequence?: number;
    fxRateFormatted?: string;
    valueFormatted?: string;
    enableDataKeyView?: boolean;
};

export type DasRowUpdate = (rowIndex: number, columnId: string, value?: string) => void;

export interface DasDataSetCellProps {
    value?: string;
    row: { index: number; original: DasDataSetRow };
    column: { id: string };
    onRowUpdated: DasRowUpdate;
    onCellUpdated: DasRowUpdate;
    editorsData: TPLOV;
}

type ValueChange = { detail: { value: string } };
type SelectChange = { detail: SelectProps.ChangeDetail };

/**
 * Cell renderer of the Step 1 grid: an editor for the row being edited, plain text otherwise. Adapted from
 * the TPA dataset grid; keep the two in sync.
 */
export const DasDataSetEditableCell = ({
    value: initialValue,
    row: { index, original },
    column: { id },
    onRowUpdated,
    editorsData,
    onCellUpdated,
}: DasDataSetCellProps) => {
    const [value, setValue] = React.useState(initialValue)
    const [dataKeyButtonClass, setDataKeyButtonClass] = React.useState('dataSourcesButtonPrimary');
    const errorMap = original.errors ?? new Map<string, string>();

    const onChange = ({ detail }: ValueChange) => {
        setValue(detail.value);
    }

    const onSelectChanged = ({ detail }: SelectChange) => {
        const selected = detail.selectedOption.value;
        if (selected == CONSTANTS.CLEAR_SELECTION) {
            setValue(undefined);
            onRowUpdated(index, id, undefined);
        } else {
            setValue(selected)
            onRowUpdated(index, id, selected);
        }
    }

    const onPeriodSelectChanged = ({ detail }: SelectChange) => {
        onCellUpdated(index, id, detail.selectedOption.value)
    }
    // Text inputs update the row on blur; selects update it on change.
    const onBlur = () => {
        onRowUpdated(index, id, value)
    }

    const selectButton = () => {
        setDataKeyButtonClass('dataSourcesButtonPrimarySelected');
        onRowUpdated(index, id, value);
    }

    const getInvalidClass = () => {
        return errorMap.has(id) ? 'dataSourcesInvalid' : '';
    }

    React.useEffect(() => {
        setValue(initialValue)
    }, [initialValue])

    const getDataSourceTypeEditor = (defaultSelectedOption: SelectProps.Option | null) => {
        // Step 1 rows pull from General Ledger or Secondary Ledger only.
        const dataSourceTypeOptions = editorsData.dataSourceTypes
            .filter((x: string) => x === CONSTANTS.DATA_SOURCE_TYPES.GENERAL_LEDGER
                || x === CONSTANTS.DATA_SOURCE_TYPES.SECONDARY_LEDGER)
            .map((x: string) => ({ label: x, value: x }));
        return <Select 
                key={`${id}_${index}`} className={"dataSourcesButton " + id + "Editor " + getInvalidClass()} 
                placeholder="Select" 
                onChange={onSelectChanged} 
                selectedOption={defaultSelectedOption} 
                options={dataSourceTypeOptions} />;
    }

    if (original.isEditing) {
        const defaultSelectedOption: SelectProps.Option | null = value == null ? null : { label: value, value };

        switch (id) {
            case CONSTANTS.DATA_SOURCE_FIELDS.DATA_SOURCE.ACCESSOR:
                return getDataSourceTypeEditor(defaultSelectedOption);
            case CONSTANTS.DATA_SOURCE_FIELDS.PERIOD.ACCESSOR:
                const periodsOptions = editorsData.periods.map((x: string) => ({ label: x, value: x }));
                return <Select key={`${id}_${index}`} className={"dataSourcesButton " + id + "Editor " + getInvalidClass()} placeholder={original.customPeriod || "Select"} onChange={onPeriodSelectChanged} selectedOption={defaultSelectedOption} options={periodsOptions} />
            case CONSTANTS.DATA_SOURCE_FIELDS.FX_TYPE.ACCESSOR:
                const fxTypeOptions = [{ label: CONSTANTS.CLEAR_SELECTION, value: CONSTANTS.CLEAR_SELECTION }, ...editorsData.currencyConversionTypes.map((x: string) => ({ label: x, value: x }))];
                return <Select key={`${id}_${index}`} filteringType="auto" className={"dataSourcesButton " + id + "Editor " + getInvalidClass()} placeholder="Select" onChange={onSelectChanged} selectedOption={defaultSelectedOption} options={fxTypeOptions} />
            case CONSTANTS.DATA_SOURCE_FIELDS.CURRENCY.ACCESSOR:
                const currencyOptions = [{ label: CONSTANTS.CLEAR_SELECTION, value: CONSTANTS.CLEAR_SELECTION }, ...editorsData.currencyCodes.map((x: string) => ({ label: x, value: x }))];
                return <Select key={`${id}_${index}`} filteringType="auto" className={"dataSourcesButton " + id + "Editor " + getInvalidClass()} placeholder="Select" onChange={onSelectChanged} selectedOption={defaultSelectedOption} options={currencyOptions} />
            case CONSTANTS.DATA_SOURCE_FIELDS.DATA_KEY.ACCESSOR:
                return <div key={`${id}_${index}`} className={dataKeyButtonClass}><Button className="showCOA" onClick={selectButton}>{value || 'Select and configure COA data'}</Button></div>
            case `${CONSTANTS.DATA_SOURCE_FIELDS.VALUE.ACCESSOR}Formatted`:
                return (
                    original.balancePullInProgress ?
                    <div key={`${id}_${index}`} className={`textCell ${id}Cell ${id}Cell_${index}`}>{getBalancePullingStatus(original)}</div>
                    :
                    <div key={`${id}_${index}`} className={`textCell ${id}Cell ${id}Cell_${index}`}>{getValueDisplay(value ?? '-', original.lastBalancePullDate)}</div>
                )
            case CONSTANTS.DATA_SOURCE_FIELDS.FX_DATE.ACCESSOR:
                return (
                        <DatePicker key={`${id}_${index}`} className={"dataSourcesDatePicker " + getInvalidClass()}
                            onChange={onChange}
                            onBlur={onBlur}
                            value={value ?? ''}
                            openCalendarAriaLabel={selectedDate =>
                                "Choose Date" +
                                (selectedDate
                                    ? `, selected date is ${selectedDate}`
                                    : "")
                            }
                            nextMonthAriaLabel="Next month"
                            placeholder="YYYY/MM/DD"
                            previousMonthAriaLabel="Previous month"
                            todayAriaLabel="Today"
                        />
                )
            case `${CONSTANTS.DATA_SOURCE_FIELDS.FX_RATE.ACCESSOR}Formatted`:
                return  <div key={`${id}_${index}`} className={"textCell " + id + "Cell"}>{value}</div>
            case 'sequence':
                return <div key={`sequence_${index}`} className="textCell sequenceCell">{value}</div>
        }
        return <Input key={`${id}_${index}`} className={"dataSourcesButton " + id + "Editor" + original.datasourceId + " " + getInvalidClass()} placeholder="Enter value" value={value ?? ''} onChange={onChange} onBlur={onBlur} />
    }
    if (id == `${CONSTANTS.DATA_SOURCE_FIELDS.VALUE.ACCESSOR}Formatted`) {
        if (original.balancePullInProgress) {
            return <div key={`${id}_${index}`} className={"cell-text " + id + "Cell"}>{getBalancePullingStatus(original)}</div>
        } else {
            return <div key={`${id}_${index}`} className={"cell-text " + id + "Cell"}>{getValueDisplay(value ?? '-', original.lastBalancePullDate)}</div>
        }
    } else if (id == CONSTANTS.DATA_SOURCE_FIELDS.DATA_KEY.ACCESSOR && original.enableDataKeyView) {
        return <div key={`${id}_${index}`} className={"cell-text " + id + "Cell"}><Link className={"cell-text dataKeyViewLink" + original.datasourceId} onFollow={() => onRowUpdated(index, id, 'view')}>{value}</Link></div>
    } else if (id == CONSTANTS.DATA_SOURCE_FIELDS.PERIOD.ACCESSOR && original.customPeriod != null) {
        return <div key={`${id}_${index}`} className={"cell-text " + id + "Cell"}>{original.customPeriod}</div>;
    } else if (id == CONSTANTS.DATA_SOURCE_FIELDS.FX_DATE.ACCESSOR || id == CONSTANTS.DATA_SOURCE_FIELDS.FX_TYPE.ACCESSOR) {
        const text = value ? (id == CONSTANTS.DATA_SOURCE_FIELDS.FX_DATE.ACCESSOR ? value.split('-').join('/') : value) : '-';
        return <div key={`${id}_${index}`} className={"cell-text " + id + "Cell"}>{text}</div>;
    }
    return <div key={`${id}_${index}`} className={"cell-text " + id + "Cell"}>{value}</div>;
}

export const dasDataSetCellEditor = {
    Cell: DasDataSetEditableCell,
};

/**
 * Row menu of the Step 1 grid. "Update value" asks the backend to pull the row's balance; it is offered on an
 * editable sheet only, while no row is being edited, and only when a handler is given. A row whose pull is in
 * flight offers only Remove. `disabled` greys the menu out, for example while a save is in flight.
 */
export function DasDataSetRowMenu(props: { row: DasDataSetRow, allowEditActions: boolean, dispatch: React.Dispatch<TPEAction>,
                                           recordBeingEdited: DatasetRecord | undefined, onUpdateValue?: (record: DatasetRecord) => void,
                                           disabled?: boolean }) {
    const { row, allowEditActions, dispatch, recordBeingEdited, onUpdateValue, disabled = false } = props;
    const menuItems = row.isEditing || (allowEditActions && row.balancePullInProgress)
        ? [{ text: "Remove", id: String(RowActions.REMOVE) }]
        : (allowEditActions ? [
            { text: "Edit", id: String(RowActions.EDIT) },
            { text: "Duplicate", id: String(RowActions.DUPLICATE) },
            { text: "Remove", id: String(RowActions.REMOVE) },
        ] : []);
    if (onUpdateValue && !row.isEditing && allowEditActions && !row.isNewRecord && !recordBeingEdited?.isEditing
        && !row.balancePullInProgress) {
        menuItems.push({ text: "Update value", id: String(RowActions.UPDATE_BALANCE) });
    }
    if (menuItems.length === 0) {
        return null;
    }
    return (
        <ButtonDropdown
            className={"rowContextMenu dataSourceRowAction" + row.datasourceId}
            items={menuItems}
            disabled={disabled}
            onItemClick={({ detail }) => {
                if (Number(detail.id) === RowActions.UPDATE_BALANCE && onUpdateValue) {
                    onUpdateValue(row);
                } else {
                    performRowAction(dispatch, recordBeingEdited, row.datasourceId, detail.id);
                }
            }}
        />
    );
}
